import { useState, useMemo } from 'react';
import { adminApi } from '../api/client';
import { useApi } from '../hooks/useApi';
import { Loading, ApiError, EmptyState } from '../components/ui/Loading';
import { Badge } from '../components/ui/Badge';
import { Modal, ConfirmModal } from '../components/ui/Modal';
import { Avatar } from '../components/ui/Avatar';
import { Pagination } from '../components/ui/Pagination';
import { useToast } from '../context/ToastContext';
import { formatDate, formatPrice, formatNumber, paginate, truncate } from '../utils/helpers';

// L'API LAVEX ne documente pas GET /admin/clients directement.
// On utilise GET /admin/stats/client/:id pour les détails.
// Pour la liste, on tente GET /admin/users?role=client (endpoint probable).

export const Clients = () => {
  const toast = useToast();

  const { data: raw, loading, error, refetch, setData } = useApi(
    () => adminApi.getClientsList()
  );

  const [statsCache,   setStatsCache]   = useState({});
  const [loadingStats, setLoadingStats] = useState(false);
  const [search,       setSearch]       = useState('');
  const [catFilter,    setCatFilter]    = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page,         setPage]         = useState(1);

  const [detailModal,    setDetailModal]    = useState(null);
  const [editModal,      setEditModal]      = useState(null);
  const [editForm,       setEditForm]       = useState({});
  const [deactivateConf, setDeactivateConf] = useState(null);
  const [catModal,       setCatModal]       = useState(null);
  const [newCat,         setNewCat]         = useState('');
  const [saving,         setSaving]         = useState(false);

  const clients = raw?.data || [];

  const filtered = useMemo(() => clients.filter(c => {
    const q = search.toLowerCase();
    const matchSearch = !q
      || c.name?.toLowerCase().includes(q)
      || c.email?.toLowerCase().includes(q)
      || c.phone?.toLowerCase().includes(q)
      || c.company_name?.toLowerCase().includes(q);
    const matchCat    = catFilter    === 'all' || c.category === catFilter;
    const matchStatus = statusFilter === 'all'
      || (statusFilter === 'active' ? c.is_active : !c.is_active);
    return matchSearch && matchCat && matchStatus;
  }), [clients, search, catFilter, statusFilter]);

  const paged = paginate(filtered, page, 12);

  const openStats = async (client) => {
    setDetailModal(client);
    if (statsCache[client.user_id]) return;
    setLoadingStats(true);
    try {
      const res = await adminApi.getClientStats(client.user_id);
      setStatsCache(p => ({ ...p, [client.user_id]: res.data }));
    } catch {}
    setLoadingStats(false);
  };

  const handleEdit = async () => {
    setSaving(true);
    try {
      await adminApi.updateUser(editModal.user_id, editForm);
      setData(prev => ({
        ...prev,
        data: prev.data.map(c => c.user_id === editModal.user_id ? { ...c, ...editForm } : c),
      }));
      toast.success('Client mis à jour ✓');
      setEditModal(null);
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (client) => {
    try {
      await adminApi.updateUser(client.user_id, { is_active: !client.is_active });
      setData(prev => ({
        ...prev,
        data: prev.data.map(c => c.user_id === client.user_id ? { ...c, is_active: !c.is_active } : c),
      }));
      toast.success(client.is_active ? 'Compte désactivé' : 'Compte réactivé ✓');
      setDeactivateConf(null);
    } catch (err) {
      toast.error(err.message || 'Erreur');
    }
  };

  const handleChangeCategory = async () => {
    setSaving(true);
    try {
      await adminApi.changeUserCategory(catModal.user_id, { category: newCat });
      setData(prev => ({
        ...prev,
        data: prev.data.map(c => c.user_id === catModal.user_id ? { ...c, category: newCat } : c),
      }));
      toast.success(`Catégorie changée → ${newCat} ✓`);
      setCatModal(null);
    } catch (err) {
      toast.error(err.message || 'Erreur lors du changement');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading text="Chargement des clients..." />;
  if (error)   return <ApiError message={error} onRetry={refetch} />;

  return (
    <div>
      <div className="filter-bar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input className="search-input" placeholder="Nom, email, téléphone, entreprise..."
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <select className="filter-select" value={catFilter} onChange={e => { setCatFilter(e.target.value); setPage(1); }}>
          <option value="all">Catégorie: Toutes</option>
          <option value="A">A — Entreprises</option>
          <option value="B">B — Particuliers</option>
        </select>
        <select className="filter-select" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="all">Statut: Tous</option>
          <option value="active">Actif</option>
          <option value="inactive">Inactif</option>
        </select>
        <span style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{filtered.length} clients</span>
        <button className="btn btn-ghost btn-sm" onClick={refetch}>↺</button>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Client</th><th>Catégorie</th><th>Points</th>
                <th>Commandes</th><th>Dépensé</th><th>Inscription</th><th>Statut</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paged.items.map(c => (
                <tr key={c.user_id}>
                  <td>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <Avatar name={c.name} />
                      <div>
                        <div className="td-main">{c.name}</div>
                        <div className="td-sub">{truncate(c.email, 28)}</div>
                        {c.company_name && (
                          <div className="td-sub" style={{ color: 'var(--green)' }}>🏢 {c.company_name}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <Badge className={c.category === 'A' ? 'badge-green' : 'badge-blue'}>Cat. {c.category}</Badge>
                  </td>
                  <td>
                    <span style={{ color: 'var(--accent)', fontWeight: 600 }}>⭐ {formatNumber(c.loyalty_points ?? 0)}</span>
                    <div className="td-sub">{formatNumber(c.total_points_earned ?? 0)} gagnés</div>
                  </td>
                  <td>
                    <div>{c.total_orders ?? '—'}</div>
                    <div className="td-sub">{c.completed_orders ?? '—'} terminées</div>
                  </td>
                  <td style={{ fontWeight: 500 }}>{formatPrice(c.total_spent)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatDate(c.created_at)}</td>
                  <td>
                    <Badge className={c.is_active ? 'badge-green' : 'badge-red'}>
                      {c.is_active ? 'Actif' : 'Inactif'}
                    </Badge>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => openStats(c)}>Voir</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => {
                        setEditModal(c);
                        setEditForm({ name: c.name, email: c.email, phone: c.phone, language: c.language });
                      }}>✎</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => { setCatModal(c); setNewCat(c.category); }}>Cat.</button>
                      <button
                        className={`btn btn-sm ${c.is_active ? 'btn-danger' : 'btn-success'}`}
                        onClick={() => c.is_active ? setDeactivateConf(c) : handleToggle(c)}
                      >
                        {c.is_active ? 'Désact.' : 'Activer'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {paged.items.length === 0 && (
            <EmptyState icon="👥" title="Aucun client" text="Modifiez les filtres ou rafraîchissez." />
          )}
        </div>
        {paged.total > 0 && <div style={{ padding: '0 16px 16px' }}><Pagination {...paged} onPage={setPage} /></div>}
      </div>

      {/* DETAIL / STATS MODAL */}
      <Modal open={!!detailModal} onClose={() => setDetailModal(null)} title={detailModal?.name} size="md">
        {loadingStats
          ? <Loading text="Chargement des statistiques..." />
          : detailModal && (() => {
              const s = statsCache[detailModal.user_id];
              return (
                <div>
                  <div className="detail-header">
                    <Avatar name={detailModal.name} size="lg" />
                    <div>
                      <div className="detail-name">{detailModal.name}</div>
                      <div className="detail-meta">{detailModal.email}</div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                        <Badge className={detailModal.category === 'A' ? 'badge-green' : 'badge-blue'}>Cat. {detailModal.category}</Badge>
                        <Badge className={detailModal.is_active ? 'badge-green' : 'badge-red'}>{detailModal.is_active ? 'Actif' : 'Inactif'}</Badge>
                      </div>
                    </div>
                  </div>
                  {s && (
                    <div className="grid-2" style={{ gap: 10, marginBottom: 16 }}>
                      {[
                        { l: '⭐ Points fidélité',  v: `${formatNumber(s.loyalty_points)} pts` },
                        { l: '📈 Total gagnés',     v: `${formatNumber(s.total_points_earned)} pts` },
                        { l: '📦 Commandes',        v: s.orders?.total },
                        { l: '✅ Terminées',        v: s.orders?.completed },
                        { l: '💰 Total dépensé',   v: formatPrice(s.orders?.total_spent) },
                        { l: '🎁 Économies promos', v: formatPrice(s.discounts?.total_saved) },
                      ].map((k, i) => (
                        <div key={i} style={{ background: 'var(--bg3)', borderRadius: 8, padding: '10px 12px' }}>
                          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 2 }}>{k.l}</div>
                          <div style={{ fontWeight: 600 }}>{k.v ?? '—'}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {[
                    { l: 'Téléphone',    v: detailModal.phone },
                    { l: 'Entreprise',   v: detailModal.company_name || '—' },
                    { l: 'Langue',       v: detailModal.language?.toUpperCase() || '—' },
                    { l: 'Inscrit le',   v: formatDate(detailModal.created_at) },
                    { l: 'Vérifié',      v: detailModal.is_verified ? '✓ Oui' : '✕ Non' },
                    ...(s ? [{ l: 'Promos utilisées', v: s.discounts?.used }] : []),
                  ].map((r, i) => (
                    <div key={i} className="info-row">
                      <span className="info-label">{r.l}</span>
                      <span className="info-value">{r.v ?? '—'}</span>
                    </div>
                  ))}
                </div>
              );
            })()
        }
      </Modal>

      {/* EDIT MODAL */}
      <Modal open={!!editModal} onClose={() => setEditModal(null)} title={`Modifier — ${editModal?.name}`} size="sm"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setEditModal(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={handleEdit} disabled={saving}>
            {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Sauvegarder'}
          </button>
        </>}
      >
        <div className="form-group">
          <label className="form-label">Nom</label>
          <input className="form-input" value={editForm.name || ''} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} />
        </div>
        <div className="form-group">
          <label className="form-label">Email</label>
          <input className="form-input" type="email" value={editForm.email || ''} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} />
        </div>
        <div className="form-group">
          <label className="form-label">Téléphone</label>
          <input className="form-input" value={editForm.phone || ''} onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))} />
        </div>
        <div className="form-group">
          <label className="form-label">Langue</label>
          <select className="form-select" value={editForm.language || 'fr'} onChange={e => setEditForm(p => ({ ...p, language: e.target.value }))}>
            <option value="fr">Français</option>
            <option value="ar">Arabe</option>
            <option value="en">Anglais</option>
          </select>
        </div>
      </Modal>

      {/* CATEGORY MODAL */}
      <Modal open={!!catModal} onClose={() => setCatModal(null)} title={`Changer catégorie — ${catModal?.name}`} size="sm"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setCatModal(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={handleChangeCategory} disabled={saving || newCat === catModal?.category}>
            {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Confirmer'}
          </button>
        </>}
      >
        <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
          Catégorie actuelle : <strong>Cat. {catModal?.category}</strong>
        </p>
        <div className="form-group">
          <label className="form-label">Nouvelle catégorie</label>
          <select className="form-select" value={newCat} onChange={e => setNewCat(e.target.value)}>
            <option value="A">A — Entreprise (laverie A + livreur A)</option>
            <option value="B">B — Particulier (laverie B + livreur B/C)</option>
          </select>
        </div>
        <div className="inline-alert inline-alert-yellow">
          <span>⚠️</span>
          <span style={{ fontSize: 12 }}>Changer la catégorie modifie l'assignation automatique des laveries et livreurs.</span>
        </div>
      </Modal>

      {/* DEACTIVATE CONFIRM */}
      <ConfirmModal
        open={!!deactivateConf}
        onClose={() => setDeactivateConf(null)}
        onConfirm={() => handleToggle(deactivateConf)}
        title="Désactiver le compte"
        message={`Êtes-vous sûr de vouloir désactiver le compte de ${deactivateConf?.name} ? Le client ne pourra plus se connecter ni passer de commandes.`}
        confirmLabel="Désactiver"
        danger
      />
    </div>
  );
};
